import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import '../styles/AttendancePage.css';
import { useToast } from '../components/Admissionform/ToastManager';
import { useNavigate } from 'react-router-dom';


const AttendancePage = () => {
  const { student_id, student_name } = useParams();
  const location = useLocation();
  const [attendanceData, setAttendanceData] = useState({
    studentId: '',
    InTime: '',
    OutTime: ''
  });
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [allAttendanceRecords, setAllAttendanceRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [showSearchForm, setShowSearchForm] = useState(false);
  const { showSuccess, showError } = useToast();
  
  // States for edit functionality
  const [editingRow, setEditingRow] = useState(null);
  const [editData, setEditData] = useState({});
  const [userRole, setUserRole] = useState('');
    const navigate = useNavigate();


  // State for date range search
  const [searchFilters, setSearchFilters] = useState({
    fromDate: '',
    toDate: '',
    searchType: 'month'
  });

  // State for delete confirmation popup
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    show: false,
    recordId: null,
    day: null
  });

  // Get current month and year, and set studentId from URL or location state
  useEffect(() => {
    const now = new Date();
    setSelectedMonth(now.getMonth() + 1);
    setSelectedYear(now.getFullYear());

    // Get user role from localStorage
    const role = localStorage.getItem('role') || 'user';
    setUserRole(role);

    let studentId = student_id;
    if (!studentId && location.state && location.state.student_id) {
      studentId = location.state.student_id;
    }

    if (studentId) {
      setAttendanceData(prev => ({
        ...prev,
        studentId: studentId
      }));

      fetchAttendanceRecords(studentId);
    }
  }, [student_id, location.state]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAttendanceData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearchFilterChange = (e) => {
    const { name, value } = e.target;
    setSearchFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle edit data change
  const handleEditDataChange = (e) => {
    const { name, value } = e.target;
    setEditData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Start editing a row
  const startEditing = (recordId, day, currentData) => {
    setEditingRow(`${recordId}-${day}`);
    setEditData({
      InTime: currentData.InTime || '',
      OutTime: currentData.OutTime || ''
    });
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingRow(null);
    setEditData({});
  };

  // Save edited attendance
  const saveEdit = async (recordId, day) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/attendance/${recordId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          day: day,
          InTime: editData.InTime,
          OutTime: editData.OutTime
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to update attendance');
      }

      showSuccess('Attendance updated successfully!');
      
      // Clear editing state first
      setEditingRow(null);
      setEditData({});
      
      // Force immediate refresh without delay
      await fetchAttendanceRecords(attendanceData.studentId);

    } catch (err) {
      showError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Show delete confirmation popup
  const deleteAttendanceDay = (recordId, day) => {
    setDeleteConfirmation({
      show: true,
      recordId: recordId,
      day: day
    });
  };

  // Handle actual deletion after confirmation
  const confirmDelete = async () => {
    const { recordId, day } = deleteConfirmation;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/attendance/${recordId}/day/${day}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to delete attendance');
      }

      showSuccess(`Attendance for day ${day} deleted successfully!`);
      
      // Force immediate refresh without delay
      await fetchAttendanceRecords(attendanceData.studentId);

    } catch (err) {
      showError(err.message);
    } finally {
      setLoading(false);
      setDeleteConfirmation({ show: false, recordId: null, day: null });
    }
  };

  // Cancel deletion
  const cancelDelete = () => {
    setDeleteConfirmation({ show: false, recordId: null, day: null });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/attendance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(attendanceData)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to create attendance');
      }

      showSuccess('Attendance recorded successfully!');
      setAttendanceData({
        studentId: attendanceData.studentId, // Keep the studentId
        InTime: '',
        OutTime: ''
      });

      // Force immediate refresh
      await fetchAttendanceRecords(attendanceData.studentId);

    } catch (err) {
      showError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendanceRecords = async (studentId) => {
    if (!studentId) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      let url = `${process.env.REACT_APP_API_URL}/attendance/${studentId}`;
      const params = new URLSearchParams();

      if (searchFilters.searchType === 'dateRange') {
        if (searchFilters.fromDate) params.append('fromDate', searchFilters.fromDate);
        if (searchFilters.toDate) params.append('toDate', searchFilters.toDate);
      } else {
        if (selectedMonth) params.append('month', selectedMonth);
        if (selectedYear) params.append('year', selectedYear);
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();

      if (!response.ok) {
        if (response.status === 404) {
          setAttendanceRecords([]);
          setAllAttendanceRecords([]);
          return;
        }
        throw new Error(result.error || 'Failed to fetch attendance records');
      }

      // Force state update with new data
      const records = result.data || [];
      setAttendanceRecords([...records]);
      setAllAttendanceRecords([...records]);

    } catch (err) {
      setError(err.message);
      setAttendanceRecords([]);
      setAllAttendanceRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (attendanceData.studentId) {
      fetchAttendanceRecords(attendanceData.studentId);
    }
  };

  const resetFilters = () => {
    const now = new Date();
    setSelectedMonth(now.getMonth() + 1);
    setSelectedYear(now.getFullYear());
    setSearchFilters({
      fromDate: '',
      toDate: '',
      searchType: 'month'
    });
    if (attendanceData.studentId) {
      fetchAttendanceRecords(attendanceData.studentId);
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '-';
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getTotalRecordsCount = () => {
    let totalCount = 0;
    attendanceRecords.forEach(record => {
      if (record.attendance_data) {
        totalCount += Object.keys(record.attendance_data).length;
      }
    });
    return totalCount;
  };

  // Delete Confirmation Popup Component
  const DeleteConfirmationPopup = () => {
    if (!deleteConfirmation.show) return null;

    return (
      <div className="popup-overlay">
        <div className="popup-content">
          <h3>Confirm Delete</h3>
          <p>Are you sure you want to delete attendance for day {deleteConfirmation.day}?</p>
          <p>This action cannot be undone.</p>
          <div className="popup-actions">
            <button 
              onClick={confirmDelete} 
              className="btn-delete-confirm"
              disabled={loading}
            >
              {loading ? 'Deleting...' : 'Yes, Delete'}
            </button>
            <button 
              onClick={cancelDelete} 
              className="btn-cancel-popup"
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderAttendanceTable = () => {
    const hasRecords = attendanceRecords.length > 0;
    
    return (
      <div className="table-container">
        <div className="attendance-record">
          <div className="record-header">
            <h3>
              Attendance for {decodeURIComponent(student_name)}
            </h3>
            <div className="record-meta">
              <span>Student ID: {student_id}</span>
              <span>NO. of Classes: {hasRecords ? getTotalRecordsCount() : 0}</span>
            </div>
          </div>

          <div className="search-toggle-container">
            <button
              type="button"
              onClick={() => setShowSearchForm(!showSearchForm)}
              className="btn-toggle-search"
            >
              {showSearchForm ? 'Hide' : 'Show'} Search Attendance
            </button>
          </div>

          {showSearchForm && (
            <div className="searchap-form" style={{ marginBottom: '20px', zIndex: 1000 }}>
              <div className="search-type-selector">
                <label>
                  <input
                    type="radio"
                    name="searchType"
                    value="month"
                    checked={searchFilters.searchType === 'month'}
                    onChange={handleSearchFilterChange}
                  />
                  Search by Month/Year
                </label>
                <label>
                  <input
                    type="radio"
                    name="searchType"
                    value="dateRange"
                    checked={searchFilters.searchType === 'dateRange'}
                    onChange={handleSearchFilterChange}
                  />
                  Search by Date Range
                </label>
              </div>

              {searchFilters.searchType === 'month' ? (
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="month">Month</label>
                    <select
                      id="month"
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                    >
                      <option value="">Select Month</option>
                      {Array.from({ length: 12 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {new Date(0, i).toLocaleString('default', { month: 'long' })}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="year">Year</label>
                    <select
                      id="year"
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value)}
                    >
                      <option value="">Select Year</option>
                      {Array.from({ length: 10 }, (_, i) => {
                        const year = new Date().getFullYear() - i;
                        return (
                          <option key={year} value={year}>{year}</option>
                        );
                      })}
                    </select>
                  </div>
                </div>
              ) : (
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="fromDate">From Date</label>
                    <input
                      type="date"
                      id="fromDate"
                      name="fromDate"
                      value={searchFilters.fromDate}
                      onChange={handleSearchFilterChange}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="toDate">To Date</label>
                    <input
                      type="date"
                      id="toDate"
                      name="toDate"
                      value={searchFilters.toDate}
                      onChange={handleSearchFilterChange}
                    />
                  </div>
                </div>
              )}

              <div className="search-actions">
                <button type="button" onClick={handleSearch} className="btn-primary">
                  Search
                </button>
                <button type="button" onClick={resetFilters} className="btn-secondary">
                  Reset Filters
                </button>
              </div>
            </div>
          )}

          <div className="attendance-table-container">
            <div className="scrollable-table">
              <table className="attendance-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Day</th>
                    <th>In Time</th>
                    <th>Out Time</th>
                    <th>Entry By</th>
                    {userRole === 'Super-Admin' && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {hasRecords ? (
                    attendanceRecords.map((record) => 
                      Object.entries(record.attendance_data).map(([day, data]) => {
                        const dayNumber = day.replace('day_', '');
                        const date = new Date(record.year, record.month - 1, parseInt(dayNumber));
                        const formattedDate = date.toLocaleDateString('en-US', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        });
                        const isEditing = editingRow === `${record.id}-${dayNumber}`;
                        
                        const rowKey = `${record.id}-${day}-${Date.now()}`;

                        return (
                          <tr key={rowKey}>
                            <td>{formattedDate}</td>
                            <td>{date.toLocaleDateString('en-US', { weekday: 'short' })}</td>
                            <td>
                              {isEditing ? (
                                <input
                                  type="time"
                                  name="InTime"
                                  value={editData.InTime}
                                  onChange={handleEditDataChange}
                                  className="edit-input"
                                />
                              ) : (
                                formatTime(data.InTime)
                              )}
                            </td>
                            <td>
                              {isEditing ? (
                                <input
                                  type="time"
                                  name="OutTime"
                                  value={editData.OutTime}
                                  onChange={handleEditDataChange}
                                  className="edit-input"
                                />
                              ) : (
                                formatTime(data.OutTime)
                              )}
                            </td>
                            <td>{data.EmpID}</td>
                            {userRole === 'Super-Admin' && (
                              <td>
                                <div className="action-buttons">
                                  {isEditing ? (
                                    <>
                                      <button
                                        onClick={() => saveEdit(record.id, dayNumber)}
                                        className="btn-save"
                                        disabled={loading}
                                        title="Save"
                                      >
                                        ✓
                                      </button>
                                      <button
                                        onClick={cancelEditing}
                                        className="btn-cancel1"
                                        title="Cancel"
                                      >
                                        ✗
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      <button
                                        onClick={() => startEditing(record.id, dayNumber, data)}
                                        className="btn-edit"
                                        title="Edit"
                                      >
                                        ✎
                                      </button>
                                      <button
                                        onClick={() => deleteAttendanceDay(record.id, dayNumber)}
                                        className="btn-delete"
                                        disabled={loading}
                                        title="Delete"
                                      >
                                        🗑
                                      </button>
                                    </>
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                      })
                    ).flat()
                  ) : (
                    <tr>
                      <td colSpan={userRole === 'Super-Admin' ? 6 : 5} className="no-records-cell">
                        {loading ? 'Loading...' : 'No attendance records found for this student.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="attendance-page">
 <div className="page-header">
      <div className="header-flex">
        <div className="header-text">
          <h1>Student Attendance Management</h1>
          <p>Record and view student attendance</p>
        </div>
        <button className="back-btn" onClick={() => navigate('/staff-dashboard')}>
          ←
        </button>
      </div>
    </div>

      <div className="attendance-form-container">
        <h2>Record Attendance - {decodeURIComponent(student_name)} ({student_id})</h2>
        <form onSubmit={handleSubmit} className="attendance-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="InTime">In Time</label>
              <input
                type="time"
                id="InTime"
                name="InTime"
                value={attendanceData.InTime}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="OutTime">Out Time</label>
              <input
                type="time"
                id="OutTime"
                name="OutTime"
                value={attendanceData.OutTime}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? 'Recording...' : 'Record Attendance'}
            </button>
          </div>
        </form>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
      </div>

      <div className="attendance-records">
        {renderAttendanceTable()}
      </div>

      {/* Delete Confirmation Popup */}
      <DeleteConfirmationPopup />
    </div>
  );
};

export default AttendancePage;